import { useState, useMemo } from "react"
import { Pencil, Inbox, Check, Save } from "lucide-react"
import { DS } from "../lib/ui"
import { prixUnitePrelevable } from "../lib/depots-helpers"
import DepotCountersList from "./DepotCountersList"           // ADR-210 — compteurs extraits
import { clampDepotQty } from "../lib/consommables-helpers"   // ADR-210 — clamp partagé
import useEchapPourFermer from "../lib/useEchapPourFermer"

// Saisie d'un dépôt (sortie stock central → logement) en UNITÉ PRÉLEVABLE.
// Schéma de l'objet retourné via onSave :
//   { id, date "YYYY-MM-DD", mois "YYYY-MM", logId, auteur "menage"|"admin",
//     initiales, lignes:[{csId, nbPrelevables, prixUnitaire}], coutTotal,
//     notes?, createdAt }
//
// Mode édition : prop `editing` = dépôt existant. Pré-remplit tout, conserve
// id et createdAt. Pour la borne max d'une ligne déjà présente dans le dépôt
// d'origine, on ajoute la quantité initiale au qtyDispo (le revert va libérer
// ce stock).
export default function SaisirDepotModal({
  allLogs, kitProduits, operProduits,
  defaultLogId, defaultAuteur = "admin",
  // ⚠️ AUCUN MONTANT CÔTÉ TERRAIN (Dimitri 2026-08-23) : par défaut, un dépôt
  // saisi par un AGENT masque les prix — c'est une information de gestion.
  hidePrices = defaultAuteur === "menage",
  stockCentral = {},
  editing, // dépôt existant à modifier (optionnel)
  erreur = null, // l'échec de l'enregistrement, dit DANS la fenêtre
  onSave, onClose,
}) {
  // Échap ferme — consigne 2026-08-14, hook partagé (pile anti-double-fermeture).
  useEchapPourFermer(onClose)
  const today = new Date().toISOString().split("T")[0]
  // Bornes du date picker côté ménage uniquement (1er du mois courant → aujourd'hui).
  // Bloque antidatage et post-datage. Admin reste libre.
  const isMenage = defaultAuteur === "menage"
  const minDate = isMenage ? `${today.slice(0, 7)}-01` : undefined
  const maxDate = isMenage ? today : undefined
  const allProds = useMemo(() => [...kitProduits, ...operProduits], [kitProduits, operProduits])
  const isEdit = !!editing

  // En mode édition, lignes initiales = celles du dépôt existant ; sinon 0 partout.
  const initLignes = () => {
    const m = Object.fromEntries(allProds.map(p => [p.id, 0]))
    if (editing?.lignes) {
      editing.lignes.forEach(l => { if (m[l.csId] != null) m[l.csId] = l.nbPrelevables })
    }
    return m
  }
  // Quantités d'origine (pour relax de la borne max en mode édition).
  const originalLignes = useMemo(() => {
    const m = Object.fromEntries(allProds.map(p => [p.id, 0]))
    if (editing?.lignes) editing.lignes.forEach(l => { if (m[l.csId] != null) m[l.csId] = l.nbPrelevables })
    return m
  }, [editing, allProds])

  const [date, setDate] = useState(editing?.date || today)
  const [logId, setLogId] = useState(editing?.logId || defaultLogId || allLogs[0]?.id || "")
  const [initiales, setInitiales] = useState(editing?.initiales || "")
  const [notes, setNotes] = useState(editing?.notes || "")
  const [lignes, setLignes] = useState(initLignes)
  const [warn, setWarn] = useState({}) // {csId: boolean} pour clignoter "stock insuffisant"
  const [saving, setSaving] = useState(false)

  // Borne max par produit. En mode édition : qtyDispo + valeur d'origine de cette ligne.
  // Hors édition : qtyDispo.
  const maxFor = (csId) => {
    const dispo = stockCentral?.[csId]?.qtyDispo || 0
    return dispo + (originalLignes[csId] || 0)
  }

  const setQty = (csId, n) => {
    const { value, clamped } = clampDepotQty(n, maxFor(csId))
    if (clamped) {
      setWarn(w => ({ ...w, [csId]: true }))
      setTimeout(() => setWarn(w => ({ ...w, [csId]: false })), 1500)
    }
    setLignes(l => ({ ...l, [csId]: value }))
  }

  const lignesActives = allProds
    .filter(p => (lignes[p.id] || 0) > 0)
    .map(p => ({
      csId: p.id,
      nbPrelevables: lignes[p.id],
      prixUnitaire: Math.round(prixUnitePrelevable(p) * 1000) / 1000,
    }))
  const coutTotal = Math.round(
    lignesActives.reduce((s, l) => s + l.nbPrelevables * l.prixUnitaire, 0) * 100
  ) / 100

  const log = allLogs.find(l => l.id === logId)
  const isSousLoc = log?.propId === "p6"
  const canSave = !!logId && lignesActives.length > 0 && !!date

  const handleSave = async () => {
    if (!canSave) return
    setSaving(true)
    const mois = date.slice(0, 7)
    const depot = isEdit ? {
      ...editing,
      date,
      mois,
      logId,
      initiales: initiales.trim().toUpperCase(),
      lignes: lignesActives,
      coutTotal,
      notes: notes.trim() || undefined,
      updatedAt: new Date().toISOString(),
      updatedBy: defaultAuteur,
    } : {
      id: "dep_" + Math.random().toString(36).slice(2, 10),
      date,
      mois,
      logId,
      auteur: defaultAuteur,
      initiales: initiales.trim().toUpperCase(),
      lignes: lignesActives,
      coutTotal,
      notes: notes.trim() || undefined,
      createdAt: new Date().toISOString(),
    }
    try {
      // En mode édition, on passe aussi les lignes d'origine pour permettre au caller
      // de calculer la diff stockCentral.
      await onSave(depot, isEdit ? editing : null)
    } finally { setSaving(false) }
  }

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",zIndex:1001,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
      <div className="fbz-modal-sheet" style={{background:DS.card,borderRadius:"20px 20px 0 0",width:"100%",maxWidth:600,maxHeight:"92vh",overflowY:"auto",fontFamily:DS.fontBody}}>
        <div style={{padding:"16px 20px",borderBottom:`1px solid ${DS.border}`,position:"sticky",top:0,background:DS.card,zIndex:2}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:7,fontFamily:DS.fontDisplay,fontWeight:600,color:DS.marineDark,fontSize:17}}>
                {isEdit ? <Pencil size={17} strokeWidth={2.2}/> : <Inbox size={18} strokeWidth={2.2}/>}{isEdit ? "Modifier le dépôt" : "Saisir un dépôt"}
              </div>
              {/* ⚠️ AUCUN MONTANT CHEZ L'AGENT (Dimitri 2026-08-23) : le coût
                  reste CALCULÉ (la compta en vit) mais ne s'affiche pas — un
                  agent de ménage n'a aucun besoin du prix de ce qu'il porte. */}
              <div style={{fontSize:11,color:DS.textMuted,marginTop:2}}>{lignesActives.length} produit{lignesActives.length>1?"s":""}{hidePrices ? "" : ` · Total ${coutTotal.toFixed(2)} €`}</div>
            </div>
            <button onClick={onClose} style={{background:"none",border:"none",fontSize:24,cursor:"pointer",color:DS.textMuted}}>×</button>
          </div>
        </div>

        <div style={{padding:"16px 20px"}}>
          {/* Métadonnées */}
          {/* ⚠️ ON NE MONTRE JAMAIS À QUELQU'UN SA PROPRE IDENTITÉ (règle
              Dimitri 2026-08-23) : l'agent est connecté à son compte, ses
              initiales ne lui apprennent rien. Le champ reste pour l'ADMIN,
              qui saisit un dépôt POUR quelqu'un d'autre — là, elles disent qui.
              La donnée, elle, part toujours (la trace ne change pas). */}
          <div style={{display:"grid",gridTemplateColumns: hidePrices ? "1fr" : "1fr 1fr",gap:10,marginBottom:14}}>
            <div>
              <label style={{display:"block",fontSize:10,fontWeight:700,color:DS.textMuted,textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>Date</label>
              <input type="date" value={date} min={minDate} max={maxDate} onChange={e=>setDate(e.target.value)}
                style={{width:"100%",border:`0.5px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,padding:"8px 10px",fontSize:13,fontFamily:DS.fontBody,outline:"none",background:DS.card,color:DS.marine}}/>
            </div>
            {!hidePrices && (
            <div>
              <label style={{display:"block",fontSize:10,fontWeight:700,color:DS.textMuted,textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>Initiales</label>
              <input type="text" value={initiales} maxLength={4} placeholder="DM"
                onChange={e=>setInitiales(e.target.value)}
                style={{width:"100%",border:`0.5px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,padding:"8px 10px",fontSize:13,fontFamily:DS.fontBody,outline:"none",background:DS.card,color:DS.marine,textTransform:"uppercase"}}/>
            </div>
            )}
          </div>

          <div style={{marginBottom:14}}>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:DS.textMuted,textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>Logement</label>
            <select value={logId} onChange={e=>setLogId(e.target.value)}
              style={{width:"100%",border:`0.5px solid ${isSousLoc ? DS.ciel : DS.borderHover}`,borderRadius:DS.radiusControl,padding:"8px 10px",fontSize:13,fontFamily:DS.fontBody,outline:"none",background:DS.card,color:DS.marine,fontWeight:600}}>
              {allLogs.map(l => (
                <option key={l.id} value={l.id}>{l.propId === "p6" ? "[Sous-loc] " : ""}{l.nom}</option>
              ))}
            </select>
          </div>

          {/* Liste produits — ADR-210 : composant extrait DepotCountersList */}
          <DepotCountersList
            kitProduits={kitProduits} operProduits={operProduits}
            stockCentral={stockCentral}
            lignes={lignes} warn={warn}
            maxFor={maxFor} onSetQty={setQty}
            originalLignes={originalLignes} isEdit={isEdit}
            hidePrices={hidePrices}
          />

          <div>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:DS.textMuted,textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>Notes (optionnel)</label>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={2}
              style={{width:"100%",border:`0.5px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,padding:"8px 10px",fontSize:12,fontFamily:DS.fontBody,outline:"none",background:DS.card,color:DS.marine,resize:"vertical"}}/>
          </div>

          <div style={{background:DS.cielSoft,borderRadius:DS.radiusCard,padding:"12px 14px",marginTop:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:12,fontWeight:700,color:DS.marineDeep,textTransform:"uppercase",letterSpacing:.5}}>Coût total</span>
            {!hidePrices && (
              <span style={{fontFamily:DS.fontDisplay,fontSize:20,fontWeight:700,color:DS.marine}}>{coutTotal.toFixed(2)} €</span>
            )}
          </div>
        </div>

        {erreur && (
          <div role="alert" data-testid="depot-erreur" style={{margin:"0 20px 10px",padding:"9px 11px",borderRadius:10,fontSize:12,background:"#F4E7F1",border:"1px solid #DDC2D7",color:"#5E2255"}}>
            {erreur}
          </div>
        )}
        <div style={{padding:"12px 20px",borderTop:`1px solid ${DS.border}`,position:"sticky",bottom:0,background:DS.card,display:"flex",gap:8}}>
          <button className="fbz-press" onClick={onClose} disabled={saving}
            style={{flex:1,padding:11,background:DS.card,color:DS.marine,border:`0.5px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,fontWeight:600,fontSize:12,cursor:"pointer",fontFamily:DS.fontBody}}>Annuler</button>
          <button className="fbz-press" onClick={handleSave} disabled={!canSave || saving}
            style={{flex:2,display:"inline-flex",alignItems:"center",justifyContent:"center",gap:7,padding:11,background:saving||!canSave?DS.borderHover:DS.corail,color:"#fff",border:"none",borderRadius:DS.radiusControl,fontWeight:700,fontSize:12,cursor:saving||!canSave?"not-allowed":"pointer",fontFamily:DS.fontBody}}>
            {saving ? "..." : <>{isEdit ? <Save size={15} strokeWidth={2.2}/> : <Check size={16} strokeWidth={2.4}/>}{isEdit ? "Enregistrer les modifications" : "Enregistrer le dépôt"}</>}
          </button>
        </div>
      </div>
    </div>
  )
}

